import type { Appointment } from "../services/appointmentService";
import { appointmentNeedsHealthRecord } from "./promptFillHealthRecord";

const ACTIVE_STATUSES = new Set([
  "scheduled",
  "confirmed",
  "pending_deposit",
  "completed",
]);

export const isActiveDashboardAppointment = (appointment: Appointment): boolean =>
  ACTIVE_STATUSES.has(appointment.status);

/** Service + extras prices are stored in cents. */
export const appointmentEstimatedCents = (appointment: Appointment): number => {
  const serviceCents =
    typeof appointment.service?.price === "number" ? appointment.service.price : 0;
  const extrasCents = (appointment.extra_services ?? []).reduce((sum, extra) => {
    const price = typeof extra.price === "number" ? extra.price : 0;
    return sum + price;
  }, 0);

  return Math.max(0, serviceCents + extrasCents);
};

export const sumAppointmentsEstimatedCents = (
  appointments: Appointment[]
): number =>
  appointments
    .filter(isActiveDashboardAppointment)
    .reduce((sum, appointment) => sum + appointmentEstimatedCents(appointment), 0);

export type DashboardPendingItem = {
  appointment: Appointment;
  relativeDays: number;
};

const daysAgoFromIso = (iso: string, todayIso: string): number => {
  const start = Date.parse(`${iso.slice(0, 10)}T00:00:00`);
  const today = Date.parse(`${todayIso}T00:00:00`);
  if (!Number.isFinite(start) || !Number.isFinite(today)) {
    return 0;
  }
  return Math.max(0, Math.round((today - start) / 86_400_000));
};

/** Appointments today that still need a health record (Sem ficha). */
export const buildHealthRecordPendings = (
  appointments: Appointment[],
  todayIso: string,
  limit = 4
): DashboardPendingItem[] =>
  appointments
    .filter(isActiveDashboardAppointment)
    .filter((appointment) => appointmentNeedsHealthRecord(appointment))
    .sort((a, b) => a.start_datetime.localeCompare(b.start_datetime))
    .slice(0, limit)
    .map((appointment) => ({
      appointment,
      relativeDays: daysAgoFromIso(appointment.start_datetime, todayIso),
    }));
